import { cn } from "@omni/utils/styles";
import { Controller, FieldValues, useFormContext } from "react-hook-form";

import { FieldLabel } from "@/components/Form/Base/fieldLabel.component";
import { FormGroup } from "@/components/Form/Base/formGroup.component";
import { Input } from "@/components/Form/Base/input.component";
import { InputGroup } from "@/components/Form/Base/inputGroup.component";
import { InputGroupText } from "@/components/Form/Base/inputGroupText.component";
import { FormFeedback } from "@/components/Form/RHF/FormElements/formFeedback.component";
import { FormValuesExploded } from "@/components/Form/RHF/FormFields/formField.types";
import { useNumericField } from "@/components/Form/RHF/FormFields/NumericField/NumericField.hooks";
import { calculateFieldState } from "@/components/Form/RHF/Utils/form.helpers";
import { useOmniFormContext } from "@/utils/forms/omniForm.provider";

type Props<T extends FieldValues> = Omit<FormValuesExploded<T>, "required"> & {
    required: boolean;
    placeholder?: string;
    unit?: string;
};

export const CalculatorField = <T extends FieldValues>({
    name,
    label,
    required,
    placeholder,
    unit
}: Props<T>) => {
    const { control } = useFormContext<T>();
    const { mode } = useOmniFormContext();

    const { onKeyDownHandler, onBlurHandler, onChangeHandler, onPasteHandler } =
        useNumericField<T>({
            name,
            required,
            defaultValue: 0
        });

    return (
        <FormGroup className={cn("tw:mb-0")}>
            <FieldLabel
                name={name}
                label={label}
                required={required}
                requiredIndication={false}
            />
            <div className={cn("tw:flex")}>
                <Controller<T>
                    control={control}
                    name={name}
                    render={({ field, fieldState, formState }) => {
                        const { isInvalid, isValid } = calculateFieldState(
                            fieldState,
                            formState,
                            mode
                        );

                        const handleOnChange = onChangeHandler(
                            field.value,
                            field.onChange
                        );

                        return (
                            <div
                                className={cn("tw:flex tw:flex-col tw:w-full")}
                            >
                                <InputGroup>
                                    <Input
                                        {...field}
                                        type="number"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        onChange={handleOnChange}
                                        onKeyDown={onKeyDownHandler}
                                        onBlur={onBlurHandler}
                                        onPaste={onPasteHandler}
                                        autoComplete="off"
                                        id={field.name}
                                        placeholder={placeholder}
                                        valid={isValid}
                                        invalid={isInvalid}
                                    />
                                    {!!unit && (
                                        <InputGroupText>{unit}</InputGroupText>
                                    )}
                                </InputGroup>
                                <FormFeedback
                                    name={field.name}
                                    label={label}
                                />
                            </div>
                        );
                    }}
                />
            </div>
        </FormGroup>
    );
};
